import { Link } from "react-router-dom";
import { AlertTriangle, Check, Image, QrCode, ScanLine, Timer, X } from "lucide-react";
import { WhatsAppCta } from "../components/WhatsAppCta";
import { useSeo } from "../lib/useSeo";

export function WhatsappQr() {
  useSeo({
    title: "API Web (QR Code) do WhatsApp",
    description:
      "Como funciona o disparo de WhatsApp via API Web (QR Code) na SendCube: conecta o numero escaneando um QR Code, sem depender de aprovacao da Meta.",
    path: "/whatsapp-web",
  });

  return (
    <>
      <section className="mx-auto max-w-6xl px-5 pb-14 pt-14 md:pt-20">
        <div className="inline-flex items-center gap-2 rounded-full border-2 border-ink bg-white px-3 py-1 text-xs font-bold">
          <QrCode size={14} className="text-brand-600" /> API Web
        </div>
        <h1 className="mt-5 max-w-[24ch] text-4xl font-extrabold leading-[1.1] md:text-5xl">
          O WhatsApp que você deseja usar, conectado por QR Code
        </h1>
        <p className="mt-5 max-w-[62ch] text-lg text-ink/70">
          Semelhante ao WhatsApp Web, você escaneia um QR Code e o número fica
          conectado à SendCube. Não precisa criar conta comercial na Meta nem esperar aprovação. Em
          minutos, você já cria a primeira campanha.
        </p>
        <div className="mt-8">
          <WhatsAppCta message="Ola! Quero conectar meu WhatsApp por QR Code na SendCube.">
            Quero conectar meu número
          </WhatsAppCta>
        </div>
      </section>

      <section className="border-y-2 border-ink bg-white py-14">
        <div className="mx-auto max-w-6xl px-5">
          <h2 className="text-2xl font-bold">Como fica mais produtivo, na prática</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            <div className="rounded-2xl border-2 border-ink bg-paper p-6 shadow-solid-sm">
              <ScanLine size={24} className="text-brand-600" />
              <h3 className="mt-3 font-bold">Confere o número antes do envio</h3>
              <p className="mt-2 text-sm text-ink/60">
                Antes de enviar a campanha, a SendCube confirma que aquele número da planilha existe de
                verdade no WhatsApp. Caso seja um número inválido, o sistema marca como "falha", evitando novas tentativas.
              </p>
            </div>
            <div className="rounded-2xl border-2 border-ink bg-paper p-6 shadow-solid-sm">
              <Timer size={24} className="text-brand-600" />
              <h3 className="mt-3 font-bold"> Você controla o intervalo entre mensagens</h3>
              <p className="mt-2 text-sm text-ink/60">
                Na SendCube, você determina o tempo entre um envio e outro da campanha. Mas, atenção: enviar mensagens em um curto intervalo,
                pode acarretar na restrição do número, pelo próprio WhatsApp.
              </p>
            </div>
            <div className="rounded-2xl border-2 border-ink bg-paper p-6 shadow-solid-sm">
              <Image size={24} className="text-brand-600" />
              <h3 className="mt-3 font-bold">Envia imagem ou áudio, junto do texto</h3>
              <p className="mt-2 text-sm text-ink/60">
                Ao anexar uma imagem ao texto ou áudio, a SendCube já converte para o formato aceito pelo WhatsApp. 
                
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-14">
        <div className="rounded-2xl border-2 border-ink bg-amber-50 p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle size={22} className="mt-0.5 flex-shrink-0 text-amber-700" />
            <div>
              <h3 className="font-bold text-amber-900">ATENÇÃO</h3>
              <p className="mt-1 text-sm text-amber-900/80">
                Evite enviar um alto volume de mensagem ou
                rápido demais, para evitar restrição ou banimento do nímero - é uma
                politica da Meta, nao da SendCube. Por isso, configure o intervalo entre mensagens com um ritmo mais conservador.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t-2 border-ink bg-white py-14">
        <div className="mx-auto max-w-6xl px-5">
          <h2 className="text-2xl font-bold">API Web ou API Oficial? Compare</h2>
          <div className="mt-8 overflow-x-auto rounded-2xl border-2 border-ink">
            <table className="w-full text-left text-sm">
              <thead className="bg-ink text-white">
                <tr>
                  <th className="px-4 py-3 font-bold">&nbsp;</th>
                  <th className="px-4 py-3 font-bold">API Web (QR Code)</th>
                  <th className="px-4 py-3 font-bold">API Oficial</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/10 bg-paper">
                <tr>
                  <td className="px-4 py-3 font-medium">Setup</td>
                  <td className="px-4 py-3">Escanear QR Code em minutos</td>
                  <td className="px-4 py-3">Requer WABA aprovada pela Meta</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">Risco de bloqueio</td>
                  <td className="px-4 py-3">Existe, se enviar rápido demais</td>
                  <td className="px-4 py-3">Não há banimento por volume, dentro do limite</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">Mensagem inicial</td>
                  <td className="px-4 py-3">Texto livre, qualquer horário</td>
                  <td className="px-4 py-3">Precisa de template aprovado pela Meta</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">Indicado para</td>
                  <td className="px-4 py-3">Começar rápido com volume moderado</td>
                  <td className="px-4 py-3">Alto volume indicado para empresas maiores</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-6 flex flex-wrap gap-4 text-sm">
            <span className="inline-flex items-center gap-2 text-ink/70">
              <Check size={16} className="text-brand-600" /> Os dois enviam pelo WhatsApp de
              verdade
            </span>
            <span className="inline-flex items-center gap-2 text-ink/70">
              <X size={16} className="text-red-600" /> Impossível utilizar os dois no mesmo número, ao
              mesmo tempo
            </span>
          </div>
          <Link to="/whatsapp-oficial" className="mt-6 inline-block text-sm font-bold text-brand-600 hover:underline">
            Ver os detalhes da API Oficial →
          </Link>
        </div>
      </section>
    </>
  );
}
